/**
 * DevBridge Utility for Local Chrome DevTools Interactivity
 * Attaches window.__EQUOTE_DB__ in development mode to inspect and manipulate IndexedDB stores.
 */
import { customerRepository, noteRepository, illustrationRepository, contractRepository } from '../repositories';
import { getIndexedDb, STORES } from './db.web';

export interface DevBridge {
  dump: () => Promise<Record<string, any[]>>;
  reset: () => Promise<void>;
  query: (storeName: 'customers' | 'notes' | 'illustrations' | 'contracts', filterObj?: Record<string, any>) => Promise<any[]>;
  exportJson: () => Promise<string>;
  seedCustom: (jsonContent: Record<string, any[]>) => Promise<void>;
}

declare global {
  interface Window {
    __EQUOTE_DB__?: DevBridge;
  }
}

export function initDevBridge(): void {
  if (typeof window === 'undefined') return;

  const bridge: DevBridge = {
    /** In toàn bộ 4 stores ra console dưới dạng JSON Object */
    async dump() {
      const [customers, notes, illustrations, contracts] = await Promise.all([
        customerRepository.getAll(),
        noteRepository.getAll(),
        illustrationRepository.getAll(),
        contractRepository.getAll(),
      ]);

      const data = { customers, notes, illustrations, contracts };
      console.log('=== [eQuote Local IndexedDB Dump] ===', data);
      return data;
    },

    /** Xóa sạch dữ liệu và nạp lại dữ liệu mầm ban đầu */
    async reset() {
      console.warn('🔄 resetting eQuote IndexedDB stores...');
      const db = await getIndexedDb();
      const tx = db.transaction([STORES.CUSTOMERS, STORES.NOTES, STORES.ILLUSTRATIONS, STORES.CONTRACTS], 'readwrite');
      await Promise.all([
        tx.objectStore(STORES.CUSTOMERS).clear(),
        tx.objectStore(STORES.NOTES).clear(),
        tx.objectStore(STORES.ILLUSTRATIONS).clear(),
        tx.objectStore(STORES.CONTRACTS).clear(),
      ]);
      await tx.done;

      // Re-trigger auto-seed
      await Promise.all([
        customerRepository.getAll(),
        noteRepository.getAll(),
        illustrationRepository.getAll(),
        contractRepository.getAll(),
      ]);

      console.log('✅ IndexedDB reset complete! Standard seed data re-populated.');
    },

    /** Truy vấn/Lọc dữ liệu trực tiếp trong một store */
    async query(storeName, filterObj) {
      let data: any[] = [];
      switch (storeName) {
        case 'customers':
          data = await customerRepository.getAll();
          break;
        case 'notes':
          data = await noteRepository.getAll();
          break;
        case 'illustrations':
          data = await illustrationRepository.getAll();
          break;
        case 'contracts':
          data = await contractRepository.getAll();
          break;
      }

      if (!filterObj) return data;

      return data.filter((item) => {
        return Object.entries(filterObj).every(([key, val]) => item[key] === val);
      });
    },

    /** Xuất chuỗi JSON stringified để copy dán vào ô chat */
    async exportJson() {
      const data = await bridge.dump();
      const jsonStr = JSON.stringify(data, null, 2);
      console.log(jsonStr);
      return jsonStr;
    },

    /** Nhập dữ liệu mầm tùy chỉnh */
    async seedCustom(jsonContent) {
      const db = await getIndexedDb();
      const tx = db.transaction([STORES.CUSTOMERS, STORES.NOTES, STORES.ILLUSTRATIONS, STORES.CONTRACTS], 'readwrite');

      if (jsonContent.customers) {
        for (const c of jsonContent.customers) await tx.objectStore(STORES.CUSTOMERS).put(c);
      }
      if (jsonContent.notes) {
        for (const n of jsonContent.notes) await tx.objectStore(STORES.NOTES).put(n);
      }
      if (jsonContent.illustrations) {
        for (const i of jsonContent.illustrations) await tx.objectStore(STORES.ILLUSTRATIONS).put(i);
      }
      if (jsonContent.contracts) {
        for (const k of jsonContent.contracts) await tx.objectStore(STORES.CONTRACTS).put(k);
      }

      await tx.done;
      console.log('✅ Custom JSON seeded successfully into IndexedDB.');
    },
  };

  window.__EQUOTE_DB__ = bridge;
  console.log('🚀 [DevBridge] Activated! Type window.__EQUOTE_DB__.dump() in Chrome Console.');
}
